import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { useIdleTimer } from 'react-idle-timer';

import { getCookie, triggerTokenRefresh } from '@/services/api-client';

type PerformLogoutHandler = (...args: any[]) => Promise<void> | void;

export type PerformLogoutRef = RefObject<PerformLogoutHandler>;

export const SESSION_TIMEOUT = 20 * 60 * 1000; // 20 minutes
export const PROMPT_BEFORE_IDLE = 2 * 60 * 1000; // 2 minutes pre-warning
export const PROMPT_BEFORE_IDLE_SECONDS = PROMPT_BEFORE_IDLE / 1000;

const REFRESH_INTERVAL = 15 * 60 * 1000;
const MAX_REFRESH_RETRIES = 3;
const RETRY_DELAY_BASE = 5000;

export interface IdleSessionTimeout {
  isOpen: boolean;
  remainingSeconds: number;
  onStayLoggedIn: () => void;
}

export interface AuthSessionScenariosReturn {
  sessionTimeout: IdleSessionTimeout;
  activate: () => void;
  setOpenTimeoutModal: (open: boolean) => void;
  setRemainingTime: (seconds: number) => void;
}

export const useAuthSessionScenarios = (
  isAuthenticated: boolean,
  tenantId: number | undefined,
  openLogoutSuccessModal: boolean,
  performLogoutRef: RefObject<PerformLogoutHandler>
): AuthSessionScenariosReturn => {
  const tokenRefreshRetryCount = useRef(0);
  const [openTimeoutModal, setOpenTimeoutModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(
    PROMPT_BEFORE_IDLE_SECONDS
  );

  // ─── Idle timer callbacks ───────────────────────────────────────────────────

  const handleOnIdle = useCallback(() => {
    if (isAuthenticated) {
      performLogoutRef.current?.(true, 'idle_timeout');
    }
  }, [isAuthenticated, performLogoutRef]);

  const handleOnPrompt = useCallback(() => {
    if (isAuthenticated) {
      setOpenTimeoutModal(true);
    }
  }, [isAuthenticated]);

  const handleOnActive = useCallback(() => {
    if (!isAuthenticated || remainingTime === 0) return;
    setOpenTimeoutModal(false);
  }, [isAuthenticated, remainingTime]);

  const { getRemainingTime, activate } = useIdleTimer({
    timeout: SESSION_TIMEOUT,
    promptBeforeIdle: PROMPT_BEFORE_IDLE,
    onIdle: handleOnIdle,
    onPrompt: handleOnPrompt,
    onActive: handleOnActive,
    debounce: 500,
    disabled: !isAuthenticated || openLogoutSuccessModal,
    crossTab: true,
    syncTimers: 200,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'click',
      'scroll',
      'input',
      'change',
    ],
  });

  // Tick down the visible countdown while the timeout prompt modal is open
  useEffect(() => {
    const interval = setInterval(() => {
      if (openTimeoutModal && remainingTime > 0) {
        setRemainingTime(Math.ceil(getRemainingTime() / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [openTimeoutModal, getRemainingTime, remainingTime]);

  const handleStayLoggedIn = useCallback(() => {
    setOpenTimeoutModal(false);
    activate();
  }, [activate]);

  // ─── Token refresh with exponential-backoff retry ───────────────────────────

  useEffect(() => {
    if (!isAuthenticated) {
      tokenRefreshRetryCount.current = 0;
      return;
    }

    const timeoutIds: ReturnType<typeof setTimeout>[] = [];

    const refreshWithRetry = async () => {
      try {
        await triggerTokenRefresh();
        tokenRefreshRetryCount.current = 0;
      } catch {
        tokenRefreshRetryCount.current += 1;

        if (tokenRefreshRetryCount.current >= MAX_REFRESH_RETRIES) {
          const id = setTimeout(() => {
            performLogoutRef.current?.(false, 'token_refresh_failed', true);
          }, 3000);
          timeoutIds.push(id);
          return;
        }

        const delay =
          RETRY_DELAY_BASE * Math.pow(2, tokenRefreshRetryCount.current - 1);
        const id = setTimeout(refreshWithRetry, delay);
        timeoutIds.push(id);
      }
    };

    const intervalId = setInterval(refreshWithRetry, REFRESH_INTERVAL);

    return () => {
      clearInterval(intervalId);
      timeoutIds.forEach(clearTimeout);
    };
  }, [isAuthenticated, performLogoutRef]);

  // ─── Cross-tab logout via cookie monitoring ─────────────────────────────────

  useEffect(() => {
    if (!isAuthenticated || !tenantId) return;

    let cookieMissingCount = 0;
    let logoutScheduled = false;
    let lastCheckTime = 0;
    const COOKIE_MISSING_THRESHOLD = 3;
    const timeoutIds: ReturnType<typeof setTimeout>[] = [];

    const checkCookieStatus = () => {
      const now = Date.now();
      if (now - lastCheckTime < 1000) return;
      lastCheckTime = now;

      const hasSessionCookie = getCookie(`sv_xid_${tenantId}`);

      if (!hasSessionCookie && isAuthenticated) {
        cookieMissingCount += 1;

        if (
          cookieMissingCount >= COOKIE_MISSING_THRESHOLD &&
          !logoutScheduled
        ) {
          logoutScheduled = true;
          const id = setTimeout(() => {
            performLogoutRef.current?.(false, 'cookie_missing', true);
          }, 2000);
          timeoutIds.push(id);
          clearInterval(intervalId);
        }
      } else if (hasSessionCookie && isAuthenticated) {
        cookieMissingCount = 0;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const id = setTimeout(checkCookieStatus, 500);
        timeoutIds.push(id);
      }
    };

    const intervalId = setInterval(checkCookieStatus, 10000);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      timeoutIds.forEach(clearTimeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, tenantId, performLogoutRef]);

  // ─── API interceptor global auth-logout events ──────────────────────────────

  useEffect(() => {
    const handleAuthLogout = (event: Event) => {
      const { reason } = (event as CustomEvent<{ reason?: string }>).detail;

      if (reason === 'token_refresh_failed') {
        performLogoutRef.current?.(false, 'token_refresh_failed', true);
        return;
      }
      performLogoutRef.current?.(false, 'session_expired', true);
    };

    globalThis.addEventListener('auth-logout', handleAuthLogout);
    return () => {
      globalThis.removeEventListener('auth-logout', handleAuthLogout);
    };
  }, [performLogoutRef]);

  return {
    sessionTimeout: {
      isOpen: openTimeoutModal,
      remainingSeconds: remainingTime,
      onStayLoggedIn: handleStayLoggedIn,
    },
    activate,
    setOpenTimeoutModal,
    setRemainingTime,
  };
};
